import { type ReactNode, useId, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PERMISSIONS, type ShipmentResponse } from '@aps/contracts';
import { Button, useToast } from '@aps/ui';
import { ApiError, api } from '../lib/api.js';
import { can } from '../lib/session.js';
import { messages } from '../i18n/messages.js';
import { useSession } from '../app/session-context.js';

/** datetime-local qiymati → ISO (server `datetime({ offset: true })` kutadi). */
function toIso(local: string): string {
  return new Date(local).toISOString();
}

/**
 * Jo'natmani KURYERGA topshirish (o'z kuryerimiz).
 *
 * Kuryer shu jo'natma do'konining faol kuryerlaridan tanlanadi; ETA majburiy
 * (status jadvali `requiresEta`). Topshirilgach jo'natma `OUT_FOR_DELIVERY` bo'ladi
 * va FAQAT shu kuryerga ko'rinadi. Yo'ldagi jo'natmada kuryer almashtiriladi.
 *
 * Blok faqat dispetcherga (`order.assign_courier`) va do'koni tanlangan jo'natmada
 * chiqadi. Server hal qiladi: o'tish mumkin emas bo'lsa (masalan, hali to'lanmagan)
 * xato matni toast'da ko'rsatiladi — klientda status grafi yo'q.
 *
 * Taksi/BTS bilan yuborish uchun oddiy "Statusni o'zgartirish" qoladi.
 */
export function CourierAssign({
  shipment,
  orderId,
}: {
  shipment: ShipmentResponse;
  orderId: string;
}): ReactNode {
  const { me, locale } = useSession();
  const t = messages(locale);
  const client = useQueryClient();
  const toast = useToast();
  const courierId = useId();
  const etaFromId = useId();
  const etaToId = useId();

  const [courier, setCourier] = useState('');
  const [etaFrom, setEtaFrom] = useState('');
  const [etaTo, setEtaTo] = useState('');

  const allowed = can(me, PERMISSIONS.ORDER_ASSIGN_COURIER);
  const shopId = shipment.shopId;
  const enRoute = shipment.statusCode === 'OUT_FOR_DELIVERY';

  const couriers = useQuery({
    queryKey: ['couriers', shopId],
    queryFn: () => api.couriers(shopId ?? ''),
    enabled: allowed && shopId !== null,
  });

  const assign = useMutation({
    mutationFn: () =>
      api.assignCourier(shipment.id, {
        courierId: courier,
        // Yo'ldagi jo'natmada ETA ixtiyoriy emas (server shartiga mos): joriy vaqt yuboriladi.
        etaFrom: etaFrom === '' ? new Date().toISOString() : toIso(etaFrom),
        ...(etaTo === '' ? {} : { etaTo: toIso(etaTo) }),
      }),
    onSuccess: () => {
      setCourier('');
      setEtaFrom('');
      setEtaTo('');
      toast.show(t.toastCourierAssigned, 'success');
      for (const key of [
        ['order', orderId],
        ['shipment', shipment.id],
        ['transitions', shipment.id],
        ['work-queue'],
        ['orders'],
      ]) {
        void client.invalidateQueries({ queryKey: key });
      }
    },
    onError: (cause: unknown) => {
      toast.show(cause instanceof ApiError ? cause.text(locale) : t.errorTitle, 'danger');
    },
  });

  if (!allowed) return null;
  if (shopId === null) return <p className="aps-muted">{t.courierNeedShop}</p>;

  const list = couriers.data ?? [];
  const current = list.find((row) => row.id === shipment.courierId);
  const name = (row: { firstName: string | null; lastName: string | null }): string =>
    [row.firstName, row.lastName].filter(Boolean).join(' ');
  const needsEta = !enRoute;
  const ready = courier !== '' && (!needsEta || etaFrom !== '') && !assign.isPending;

  return (
    <div className="adm-courier-assign">
      <h3 className="adm-panel__subtitle">{t.courierAssignTitle}</h3>
      {shipment.courierId !== null && (
        <p>
          {t.courierCurrent}: <strong>{current === undefined ? '—' : name(current)}</strong>
        </p>
      )}
      {couriers.isSuccess && list.length === 0 ? (
        <p className="aps-muted">{t.courierNone}</p>
      ) : (
        <>
          <p className="aps-muted">{t.courierHint}</p>
          <label className="aps-field__label" htmlFor={courierId}>
            {t.courierSelect}
          </label>
          <select
            id={courierId}
            className="aps-field__input"
            value={courier}
            onChange={(event) => {
              setCourier(event.target.value);
            }}
          >
            <option value="">{t.courierSelect}</option>
            {list.map((row) => (
              <option key={row.id} value={row.id}>
                {`${name(row)}${row.phone === null ? '' : ` · ${row.phone}`}`}
              </option>
            ))}
          </select>
          {needsEta && (
            <>
              <label className="aps-field__label" htmlFor={etaFromId}>
                {t.courierEtaFrom}
              </label>
              <input
                id={etaFromId}
                className="aps-field__input"
                type="datetime-local"
                value={etaFrom}
                onChange={(event) => {
                  setEtaFrom(event.target.value);
                }}
              />
              <label className="aps-field__label" htmlFor={etaToId}>
                {t.courierEtaTo}
              </label>
              <input
                id={etaToId}
                className="aps-field__input"
                type="datetime-local"
                value={etaTo}
                onChange={(event) => {
                  setEtaTo(event.target.value);
                }}
              />
            </>
          )}
          <Button
            size="sm"
            disabled={!ready}
            onClick={() => {
              assign.mutate();
            }}
          >
            {enRoute ? t.courierChangeButton : t.courierAssignButton}
          </Button>
        </>
      )}
    </div>
  );
}
